import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useContent } from "@/contexts/ContentContext";
import BrandLogo from "@/components/BrandLogo";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { content } = useContent();
  const t = content?.auth?.login || {};
  const logo = content?.navbar?.logo_text || "Conexus Foundation";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <BrandLogo size={40} iconSize={22}
            markClassName="bg-emerald-700"
            nameClassName="font-heading text-xl font-semibold text-stone-900"
            showName={false} />
          <span className="font-heading text-xl font-semibold text-stone-900">{logo}</span>
        </Link>

        <div className="bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-semibold text-stone-900 mb-1">{t.title || "Welcome back"}</h1>
          <p className="font-body text-sm text-stone-500 mb-6">{t.subtitle || "Sign in to access earth monitoring data"}</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-stone-500 font-body block mb-1.5">{t.email_label || "Email"}</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-body focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                placeholder={t.email_placeholder || "you@example.com"} data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 font-body block mb-1.5">{t.password_label || "Password"}</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-body focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 pr-10"
                  placeholder={t.password_placeholder || "Your password"} data-testid="login-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? (content?.common?.buttons?.loading || "Signing in...") : (t.submit_label || "Sign In")}
            </button>
          </form>

          <p className="text-center text-sm text-stone-500 mt-6 font-body">
            {t.no_account_text || "Don't have an account?"}{" "}
            <Link to="/register" className="text-emerald-700 font-medium hover:underline" data-testid="login-register-link">
              {t.create_one_link || "Create one"}
            </Link>
          </p>
          <p className="text-center text-xs text-stone-400 mt-2 font-body">
            <Link to="/forgot-password" className="hover:text-emerald-700" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
